import type { MouseEvent } from "react";
import { useState } from "react";
import { Box, Popover, useTheme, Fade } from "@mui/material";
import { FoodsIconButton, FoodsTooltip } from "@foodpilot/foods";
import { REACTION_TYPES, ReactionType, getReactionConfig } from "../../types/reaction.types";
import { useTranslation } from "react-i18next";
import { ReactionIcon } from "../ReactionIcon";

interface PostReactionsProps {
    reactions: Record<string, number>;
    userReaction?: string;
    onReact: (type: ReactionType) => void;
    compact?: boolean;
}

export const PostReactions = ({ reactions, userReaction, onReact, compact = false }: PostReactionsProps) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const { t } = useTranslation();

    const handleReactionClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleReactionSelect = (type: ReactionType) => {
        onReact(type);
        handleClose();
    };

    const open = Boolean(anchorEl);
    const hasReactions = Object.keys(reactions).length > 0;
    const activeReaction = getReactionConfig(userReaction ?? "");

    if (compact && hasReactions) {
        const activeReactions = Object.entries(reactions).filter(([, count]) => count > 0);

        return (
            <Box display="flex" alignItems="center">
                <Box display="flex" alignItems="center" sx={{ mr: 0.5 }}>
                    {activeReactions.map(([type], index) => {
                        const reactionConfig = getReactionConfig(type);
                        if (!reactionConfig) return null;

                        return (
                            <Box
                                key={type}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 24,
                                    height: 24,
                                    borderRadius: "50%",
                                    backgroundColor: theme.palette.common.white,
                                    border: `1px solid ${theme.custom.grey[300]}`,
                                    ml: index > 0 ? -0.75 : 0,
                                    zIndex: activeReactions.length - index,
                                    cursor: "pointer",
                                }}
                                onClick={() => onReact(type as ReactionType)}
                            >
                                <ReactionIcon config={reactionConfig} size={1} />
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    }

    return (
        <>
            <FoodsTooltip title={t("social.post.react_to_post", "React to this post")}>
                {activeReaction && "muiIcon" in activeReaction && activeReaction.muiIcon ?
                    <Box
                        component="span"
                        onClick={handleReactionClick}
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: activeReaction.color,
                            cursor: "pointer",
                        }}
                    >
                        <ReactionIcon config={activeReaction} size={1} />
                    </Box>
                :   <FoodsIconButton
                        size="xs"
                        icon={activeReaction?.icon ?? "starEmpty"}
                        buttonProps={{
                            onClick: handleReactionClick,
                            sx: {
                                color: activeReaction?.color ?? theme.custom.grey[1400],
                            },
                        }}
                    />
                }
            </FoodsTooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                TransitionComponent={Fade}
                sx={{
                    "& .MuiPopover-paper": {
                        borderRadius: "24px",
                        boxShadow: theme.custom.shadows.grey,
                        border: `1px solid ${theme.custom.grey[300]}`,
                        overflow: "visible",
                        mt: -1,
                    },
                }}
            >
                <Box display="flex" alignItems="center" sx={{ p: 1, gap: 0.5 }}>
                    {REACTION_TYPES.map((reaction) => (
                        <FoodsTooltip key={reaction.type} title={t(reaction.labelKey, reaction.defaultLabel)}>
                            <Box
                                component="button"
                                type="button"
                                onClick={() => handleReactionSelect(reaction.type as ReactionType)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 40,
                                    height: 40,
                                    border: "none",
                                    borderRadius: "50%",
                                    cursor: "pointer",
                                    backgroundColor:
                                        userReaction === reaction.type ? `${reaction.color}20` : "transparent",
                                    transform: userReaction === reaction.type ? "scale(1.1)" : "scale(1)",
                                    "&:hover": {
                                        backgroundColor: `${reaction.color}20`,
                                        transform: "scale(1.2)",
                                    },
                                    transition: "all 0.2s ease",
                                }}
                            >
                                <ReactionIcon config={reaction} size={2.5} />
                            </Box>
                        </FoodsTooltip>
                    ))}
                </Box>
            </Popover>
        </>
    );
};
